/**
 * Assessments another module coordinates that this module contributes to.
 *
 * Read only here: the coordinating module owns the assessment, and this
 * module's share of it is what the reader needs to see.
 *
 * @module components/studio/panels/IncomingAssessments
 */

import React from "react";

import { Alert, Badge, Heading, MutedText, Panel, Text } from "@/components/ui";
import type { ModuleWorkspaceRecord } from "@/platform";
import type { MIMLO } from "@/types";

export function IncomingAssessments({
  assessments,
  mimlos,
  versions,
}: {
  assessments: ModuleWorkspaceRecord["incomingCrossModuleAssessments"];
  mimlos: readonly MIMLO[];
  versions: ModuleWorkspaceRecord["versions"];
}): React.ReactElement | null {
  if (!assessments.length) {
    return null;
  }

  return (
    <section className="mt-5" data-testid="module-workspace-incoming-assessments">
      <Heading level={3}>Shared assessments led by another module</Heading>
      <MutedText>
        These assessments include this module. The coordinating module controls the assessment,
        while your module contributes the weighting and outcomes shown here.
      </MutedText>
      <Panel tone="inset" flush className="mt-3 divide-y divide-base-300">
        {assessments.map((assessment, index) => {
          const mapped = assessment.mimloIds
            .map((mimloId) => mimlos.find((mimlo) => mimlo.id === mimloId))
            .filter((mimlo): mimlo is MIMLO => Boolean(mimlo));
          const versionLabels = assessment.versionIds
            .map((versionId) => versions.find((version) => version.id === versionId)?.label)
            .filter((label): label is string => Boolean(label));
          return (
            <article
              key={`${assessment.coordinatingModuleId}:${assessment.assessmentId}`}
              className="p-4"
              data-testid={`module-workspace-incoming-assessment-${index + 1}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <MutedText>
                    Coordinated by{" "}
                    {assessment.coordinatingModuleCode
                      ? `${assessment.coordinatingModuleCode} — `
                      : ""}
                    {assessment.coordinatingModuleTitle}
                  </MutedText>
                  <Heading level={4} size="md">
                    {assessment.title}
                  </Heading>
                  <MutedText>
                    {assessment.type}
                    {assessment.indicativeWeek
                      ? `, programme week ${assessment.indicativeWeek}`
                      : ""}
                    {assessment.outsideTeachingPeriod ? ", outside teaching period" : ""}
                  </MutedText>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge tone="primary" variant="soft">
                    {assessment.contributionWeighting}% of this module
                  </Badge>
                  <Badge tone="neutral">
                    {assessment.coordinatingWeighting}% in coordinating module
                  </Badge>
                </div>
              </div>
              {versionLabels.length ? (
                <Text className="mt-3">
                  <span className="font-semibold">Programme versions:</span>{" "}
                  {versionLabels.join(", ")}
                </Text>
              ) : null}
              <div className="mt-3">
                <Heading level={4} size="md">
                  Mapped module outcomes
                </Heading>
                {mapped.length ? (
                  <ul className="mt-2 space-y-2">
                    {mapped.map((mimlo) => {
                      const mimloIndex = mimlos.findIndex((candidate) => candidate.id === mimlo.id);
                      return (
                        <li key={mimlo.id}>
                          <Alert tone="info" role="none">
                            <span>
                              <span className="font-semibold">Outcome {mimloIndex + 1}:</span>{" "}
                              {mimlo.text || "No outcome wording has been added yet."}
                            </span>
                          </Alert>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <Alert tone="warning" className="mt-2">
                    No outcomes from this module are mapped yet.
                  </Alert>
                )}
              </div>
              {assessment.isCapstone || assessment.isHurdle || assessment.mandatoryPass ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {assessment.isCapstone ? <Badge tone="neutral">Capstone</Badge> : null}
                  {assessment.isHurdle ? <Badge tone="neutral">Hurdle</Badge> : null}
                  {assessment.mandatoryPass ? (
                    <Badge tone="neutral">
                      Mandatory pass
                      {assessment.minimumPassMark ? ` (${assessment.minimumPassMark}%)` : ""}
                    </Badge>
                  ) : null}
                </div>
              ) : null}
            </article>
          );
        })}
      </Panel>
    </section>
  );
}

export default IncomingAssessments;
